import type { CallLogPage } from "./call-logs";
import { api } from "./client";
import type {
  ActivityItem,
  ActivityKind,
  CustomerCallsParams,
  CustomerJobsParams,
  Named,
} from "./customers";
import type { TicketJob, TicketRow } from "./tickets";

/**
 * Client for bella-admin-api's champs module (`/v1/champs/*`): the champ panel
 * and the full champ page. Types mirror the Phase 1 contract (champ-panel
 * contract.md, 2026-10-28) field for field.
 *
 * `champs.view` opens everything except the phone reveal (`champs.phone.view`)
 * and the login block (`champs.login-block`). The data of other screens still
 * answers to their own keys: tickets and notes need `tickets.view` (notes are
 * ticket entries), the WhatsApp, ticket and call items are left out of the
 * activity (named in `hidden`) without theirs, and the calls route is 403.
 *
 * Phone numbers are always masked ("••••• •4821") except in `revealChampPhone`.
 */

export type ChampRole = "champ" | "area_manager";
export type ChampStatus =
  "prospect" | "in_training" | "missed_training" | "active" | "inactive";

/** A staff note on one of the champ's own tickets. */
export interface ChampNote {
  /** The ticket entry id. */
  id: number;
  text: string;
  author: Named | null;
  /** Bots and crons write notes on hiring tickets too. */
  authorKind: "staff" | "automation";
  ticketId: number;
  /** As `TicketRow.number`. */
  ticketNumber: string;
  createdAt: number;
}

/* ------------------------------------------------------------------ *
 * Profile
 * ------------------------------------------------------------------ */

export type AttendanceToday = "absent" | "leave" | "job_done" | "marked" | "pending";

export interface ChampTraining {
  /** YYYY-MM-DD. */
  date: string;
  label: string;
  location: string | null;
  done: boolean;
}

export type ChampAttentionItem =
  | { kind: "absent_today"; severity: "high"; status: "absent" | "leave" }
  | { kind: "jobs_overdue_today"; severity: "high"; count: number }
  | {
      kind: "tickets_about_jobs";
      /** High when `count7d` is 3 or more; shown from 1. */
      severity: "high" | "medium";
      count7d: number;
      open: number;
      latestTicketId: number;
      latestTicketNumber: string;
      latestTitle: string;
    }
  | {
      kind: "leaving";
      severity: "medium";
      reason: "job_left" | "notice";
      noticeUntil: string | null;
    }
  | {
      kind: "login_blocked_admin";
      severity: "medium";
      by: Named | null;
      at: number | null;
    }
  | { kind: "several_supervisors"; severity: "medium"; names: string[] };

export type ChampLoginState = "allowed" | "blocked_otp" | "blocked_admin";

export interface ChampProfile {
  id: number;
  /** "CH1234". */
  code: string | null;
  name: string;
  role: ChampRole;
  status: ChampStatus;
  /** The pipeline status' name ("Active", "Job Left"); null if unknown. */
  pipelineStatus: string | null;
  isDeactivated: boolean;
  onWorkNow: boolean;
  /** Today's attendance row only. */
  attendanceToday: AttendanceToday | null;
  login: {
    state: ChampLoginState;
    blockedBy: Named | null;
    blockedAt: number | null;
  };
  phone: string;
  /** Null when empty or the same as `phone`. */
  whatsapp: string | null;
  alternative: string | null;
  emergency: string | null;
  supervisor: Named | null;
  /** More active supervisor rows than one — a data problem. */
  otherActiveSupervisors: Named[];
  trainer: Named | null;
  areas: string[];
  subArea: string | null;
  city: string | null;
  reach: {
    normalKm: number | null;
    extendedKm: number | null;
    effectiveKm: number | null;
  };
  vehicle: string | null;
  /** As stored (a code). */
  language: string | null;
  availability: { from: string; to: string } | null;
  /** YYYY-MM-DD. */
  activatedOn: string | null;
  reactivatedOn: string | null;
  deactivatedOn: string | null;
  /** Not active from the START of this day. */
  noticeUntil: string | null;
  rating: number | null;
  tags: { private: string[]; public: string[] };
  training: { latest: ChampTraining | null; next: ChampTraining | null };
  stats: {
    jobsToday: {
      scheduled: number;
      done: number;
      missed: number;
      notAtHome: number;
      pending: number;
    };
    carsServed: number;
    customersServed: number;
    ticketsAboutJobs30d: number;
    daysWorkedThisMonth: number;
  };
  /** At most 6, in the contract's order. */
  attention: ChampAttentionItem[];
  /** The latest 3 (needs tickets.view; empty otherwise). */
  notes: ChampNote[];
  /** He has a ticket to write on. */
  canAddNote: boolean;
}

export interface ChampRevealedPhones {
  phone: string;
  whatsapp: string | null;
  alternative: string | null;
  emergency: string | null;
}

/* ------------------------------------------------------------------ *
 * Activity
 * ------------------------------------------------------------------ */

type TicketActivity = Extract<ActivityItem, { kind: "ticket" }>;
type JobActivity = Extract<ActivityItem, { kind: "job" }>;

/**
 * Same paging, cursor and `hidden` as the customer activity. His own tickets'
 * replies can be `from: "champ"`; a job item names its customer; a note is a
 * `ChampNote`.
 */
export type ChampActivityItem =
  | Extract<ActivityItem, { kind: "whatsapp" | "call" }>
  | (Omit<TicketActivity, "from"> & {
      from: TicketActivity["from"] | "champ";
    })
  | (JobActivity & { customer: Named | null })
  | { kind: "note"; id: string; at: number; note: ChampNote };

export interface ChampActivityPage {
  /** Newest first. */
  items: ChampActivityItem[];
  nextCursor: string | null;
  /** Kinds left out for lack of permission. */
  hidden: ("whatsapp" | "ticket" | "call")[];
}

export interface ChampActivityParams {
  kinds?: ActivityKind[];
  numberId?: number | null;
  limit?: number;
}

/* ------------------------------------------------------------------ *
 * Lists
 * ------------------------------------------------------------------ */

export interface ChampJob extends TicketJob {
  timeSlot: string | null;
  customer: Named | null;
  society: string | null;
  vehicleModel: string | null;
}

export interface ChampCustomerRow {
  subscriptionId: number;
  customer: Named;
  society: string | null;
  vehicleNumber: string;
  vehicleModel: string | null;
  planName: string | null;
  frequency: string | null;
  timeSlot: string | null;
  status: "active" | "paused" | "payment_pending";
}

export interface ChampTickets {
  /** Open first, then the latest 20 closed. */
  own: TicketRow[];
  /** Support tickets whose job is one of his, last 90 days, newest first. */
  aboutJobs: TicketRow[];
}

/* ------------------------------------------------------------------ *
 * Keys and calls
 * ------------------------------------------------------------------ */

export const champKeys = {
  all: ["champs"] as const,
  profiles: () => [...champKeys.all, "profile"] as const,
  profile: (id: number) => [...champKeys.profiles(), id] as const,
  activities: (id: number) => [...champKeys.all, "activity", id] as const,
  activity: (id: number, params: ChampActivityParams) =>
    [...champKeys.activities(id), params] as const,
  jobs: (id: number, params: CustomerJobsParams) =>
    [...champKeys.all, "jobs", id, params] as const,
  customers: (id: number) => [...champKeys.all, "customers", id] as const,
  tickets: (id: number) => [...champKeys.all, "tickets", id] as const,
  calls: (id: number, params: CustomerCallsParams) =>
    [...champKeys.all, "calls", id, params] as const,
};

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

export const getChampProfile = (id: number, signal?: AbortSignal) =>
  api.get<ChampProfile>(`/champs/${id}`, withSignal(signal));

export function getChampActivity(
  id: number,
  params: ChampActivityParams,
  cursor: string | null,
  signal?: AbortSignal,
) {
  const query = new URLSearchParams();
  if (cursor) query.set("cursor", cursor);
  if (params.limit) query.set("limit", String(params.limit));
  if (params.kinds?.length) query.set("kinds", params.kinds.join(","));
  if (params.numberId) query.set("numberId", String(params.numberId));
  const text = query.toString();
  const suffix = text ? `?${text}` : "";
  return api.get<ChampActivityPage>(
    `/champs/${id}/activity${suffix}`,
    withSignal(signal),
  );
}

/** Service jobs, newest date first. `from`/`to` default to today on the server. */
export function getChampJobs(
  id: number,
  params: Pick<CustomerJobsParams, "from" | "to">,
  signal?: AbortSignal,
) {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  return api.get<ChampJob[]>(
    `/champs/${id}/jobs?${query.toString()}`,
    withSignal(signal),
  );
}

export const getChampCustomers = (id: number, signal?: AbortSignal) =>
  api.get<ChampCustomerRow[]>(`/champs/${id}/customers`, withSignal(signal));

export const getChampTickets = (id: number, signal?: AbortSignal) =>
  api.get<ChampTickets>(`/champs/${id}/tickets`, withSignal(signal));

export function getChampCalls(
  id: number,
  params: CustomerCallsParams,
  signal?: AbortSignal,
) {
  const query = new URLSearchParams({
    from: params.from,
    to: params.to,
    page: String(params.page),
    pageSize: String(params.pageSize),
  });
  return api.get<CallLogPage>(
    `/champs/${id}/calls?${query.toString()}`,
    withSignal(signal),
  );
}

/** Add only — a champ note is a ticket entry, so there is no edit or remove. */
export const addChampNote = (id: number, input: { text: string }) =>
  api.post<ChampNote>(`/champs/${id}/notes`, input);

/** POST so nothing caches it; the server logs who looked, never the number. */
export const revealChampPhone = (id: number) =>
  api.post<ChampRevealedPhones>(`/champs/${id}/phone`);

export const blockChampLogin = (id: number) =>
  api.post<unknown>(`/champs/${id}/login-block`);

export const unblockChampLogin = (id: number) =>
  api.post<unknown>(`/champs/${id}/login-unblock`);

/** The server's code when a champ has no ticket to write a note on. */
export const CHAMP_HAS_NO_TICKET = "CHAMP_HAS_NO_TICKET";
